import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FC, useId, useState } from 'react';
import { Tick } from '@agria/theme/src/components/icon/icons/Tick';
import { space } from '../../../styles/tokens';
import { createMarkup, useResponsiveType } from '../../../utils';
import { Typography } from '../../typography/Typography';
import { BaseFormProps, StyledBase } from '../base/Base';
import { FormField, FormFieldProps } from '../formField/FormField';
import tokens from './tokens';
import baseTokens from '../base/tokens';

export interface ButtonSelectProps
  extends BaseFormProps,
    Omit<FormFieldProps, 'value'> {
  options: ButtonSelectOptions[];
  onChange: (...event: any[]) => void;
  stackOnMobile?: boolean;
  smallGap?: boolean;
  value?: string | number | boolean;
  defaultValue?: string | number | boolean;
}

export interface ButtonSelectOptions {
  /*
   * The name of the option. If you pass markup please also pass a plain-text label.
   */
  name: string;
  /*
   * A plain-text label to be used for the aria-label attribute.
   */
  label?: string;
  description?: string;
  value: string | boolean;
  disabled?: boolean;
}

const StyledRadioGroup = styled.div<{
  stackOnMobile?: boolean;
  smallGap?: boolean;
}>`
  ${({ theme: { maxMedia }, stackOnMobile, smallGap }) => css`
    display: flex;
    gap: ${smallGap ? space.xxSmall : space[tokens.gap] || space.medium};

    @media (max-width: ${maxMedia.small}) {
      ${(tokens.stacked || stackOnMobile) &&
      css`
        flex-direction: column;
        gap: ${space.xxSmall};
      `}
    }
  `}
`;

export const StyledSelectButton = styled(StyledBase)`
  ${({ theme: { colors }, invalid }) => css`
    flex-grow: 1;
    justify-content: center;
    align-items: center;
    ${useResponsiveType('bodyMediumBold')};
    display: grid;
    gap: ${space.xxSmall};

    svg {
      width: ${space.medium};
      height: ${space.medium};
    }

    :disabled {
      border: none;
      background: ${colors.brandTwo20};
      color: ${colors.brandTwo50};
      cursor: default;
    }

    &[aria-checked='true'] {
      background: ${colors[tokens.backgroundSelected] || colors.brandOne100};
      border-color: ${colors[tokens.backgroundSelected] || colors.brandOne100};
      color: ${colors.utilityWhite};
      cursor: default;
    }

    ${invalid &&
    css`
      &:focus-visible {
        ${tokens.focusInvalidBoxShadow
          ? `box-shadow: ${tokens.focusInvalidBoxShadow}`
          : `box-shadow: 0 0 0 1px ${colors.systemError}`};
      }
    `};
  `}
`;

const StyledSelectButtonText = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space.xxxSmall};
  margin-inline: -${space[baseTokens.formFieldPaddingLarge]};

  button:not([aria-checked='true'], :disabled):hover & {
    ${tokens.includeUnderline ? 'text-decoration: underline' : ''};
  }
`;

export const ButtonSelect = React.forwardRef<HTMLDivElement, ButtonSelectProps>(
  (
    {
      label,
      id,
      invalid,
      valid,
      defaultValue,
      placeholder,
      required,
      errorMessage,
      hideLabel,
      disabled,
      hintText,
      optional,
      extraInformation,
      options,
      onChange,
      stackOnMobile,
      smallGap,
      value,
      ...rest
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<
      string | number | boolean | undefined
    >(value ?? defaultValue);

    const handleOnChange = (newValue: string | number | boolean) => {
      setInputValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    };
    const errorMessageElementId = useId();

    return (
      <FormField
        fieldAs="fieldset"
        label={label}
        htmlFor={id}
        invalid={invalid}
        errorMessage={errorMessage}
        errorMessageElementId={errorMessageElementId}
        hideLabel={hideLabel}
        hintText={hintText}
        disabled={disabled}
        optional={optional}
        extraInformation={extraInformation}
        {...rest}
      >
        <StyledRadioGroup
          role="radiogroup"
          id={id}
          stackOnMobile={stackOnMobile}
          smallGap={smallGap}
          ref={ref}
        >
          {options.map((option, index) => (
            <StyledSelectButton
              key={option.value as string}
              as="button"
              type="button"
              onClick={() => handleOnChange(option.value)}
              role="radio"
              aria-checked={inputValue === option.value}
              aria-disabled={inputValue === option.value}
              aria-label={option?.label}
              aria-labelledby={`${id}-${index}-text`}
              aria-describedby={errorMessageElementId}
              tabIndex={inputValue !== option.value ? 0 : -1}
              invalid={invalid}
              id={`${id}-${index}`}
              large
              disabled={option.disabled}
            >
              <StyledSelectButtonText>
                {inputValue === option.value && tokens.includeTick && (
                  <Tick aria-hidden="true" />
                )}
                <Typography
                  component="strong"
                  variant="bodyMediumBold"
                  id={`${id}-${index}-text`}
                >
                  {createMarkup(option.name)}
                </Typography>
              </StyledSelectButtonText>
              {option.description && (
                <Typography component="span" variant="bodySmall">
                  {option.description}
                </Typography>
              )}
            </StyledSelectButton>
          ))}
        </StyledRadioGroup>
      </FormField>
    );
  }
);

ButtonSelect.displayName = 'ButtonSelect';
